import React, { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

import { getHomeStatConfig } from '../../Utils/frontStageApi';
import { updateHomeStatConfig, type HomeStatConfigType } from '../../Utils/backStageApi';

type StatisticsConfigForm = {
  vcIssuanceUnits: string;
  vcVerificationUnits: string;
};

const defaultForm: StatisticsConfigForm = {
  vcIssuanceUnits: '0',
  vcVerificationUnits: '0',
};

type FieldConfig = {
  key: keyof StatisticsConfigForm;
  labelKey: string;
  fallback: string;
};

const FIELDS: FieldConfig[] = [
  //{ key: 'vcIssuanceUnits', labelKey: 'backstage.statistics.vcIssuanceUnits', fallback: '憑證發證單位數' },
  { key: 'vcVerificationUnits', labelKey: 'backstage.statistics.vcVerificationUnits', fallback: '憑證驗證單位數' },
];

const StatisticsManagerPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [form, setForm] = useState<StatisticsConfigForm>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState({
    text: '',
    visible: false,
    isError: false,
    errorMsg: '密碼錯誤',
  });

  useEffect(() => {
    let mounted = true;

    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await getHomeStatConfig();
        if (!mounted) return;
        if (!res?.applied) return;
        setForm({
          vcIssuanceUnits: String(res.vc_issuance_units ?? 0),
          vcVerificationUnits: String(res.vc_verification_units ?? 0),
        });
      } catch {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchConfig();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (field: keyof StatisticsConfigForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openPasswordModal = () => {
    setPassword({ text: '', visible: false, isError: false, errorMsg: '密碼錯誤' });
    onOpen();
  };

  const handleConfirm = async () => {
    setPassword((prev) => ({ ...prev, isError: false }));
    setSaving(true);

    try {
      const payload: HomeStatConfigType = {
        vc_issuance_units: Number(form.vcIssuanceUnits || 0),
        vc_verification_units: Number(form.vcVerificationUnits || 0),
      };

      const res = await updateHomeStatConfig(payload, password.text);

      if (!res?.applied) {
        setPassword((prev) => ({
          ...prev,
          isError: true,
          errorMsg: res?.error_msg || '密碼錯誤',
        }));
        return;
      }

      onClose();
      toast({
        title: t('backstage.statistics.saveSuccess', '統計設定已更新'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch {
      setPassword((prev) => ({ ...prev, isError: true, errorMsg: '儲存失敗，請再試一次' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex w="100%" minH="320px" align="center" justify="center">
        <Spinner w="56px" h="56px" thickness="5px" speed="0.75s" color="#00AAFF" emptyColor="rgba(0,0,0,0.12)" />
      </Flex>
    );
  }

  return (
    <>
      <Flex w="100%" direction="column" align="center" pt="50px" pb="100px">
        <Text
          fontFamily="'Open Sans', sans-serif"
          fontSize="30px"
          fontWeight={600}
          lineHeight="133%"
          letterSpacing="0.01em"
          color="#000000"
          textAlign="center"
          mb="50px"
        >
          {t('backstage.statistics.title', '首頁統計顯示設定')}
        </Text>

        <Flex direction="column" gap="0" w="100%" maxW="640px">
          {FIELDS.map((field, idx) => (
            <Flex
              key={field.key}
              align="center"
              justify="space-between"
              py="20px"
              borderTop={idx === 0 ? '1px solid #E2E8F0' : undefined}
              borderBottom="1px solid #E2E8F0"
              gap="24px"
            >
              <Text
                fontFamily="'Open Sans', sans-serif"
                fontSize="16px"
                fontWeight={600}
                color="#000000"
                flexShrink={0}
              >
                {t(field.labelKey, field.fallback)}
              </Text>

              <Input
                value={form[field.key]}
                onChange={handleChange(field.key)}
                inputMode="numeric"
                w="160px"
                flexShrink={0}
                textAlign="right"
                fontFamily="'Open Sans', sans-serif"
                fontSize="16px"
                fontWeight={500}
                color="#000000"
                bg="transparent"
                border="1px solid #CBD5E0"
                borderRadius="6px"
                px="12px"
                h="40px"
                _focus={{ borderColor: '#00AAFF', boxShadow: '0 0 0 1px #00AAFF' }}
              />
            </Flex>
          ))}
        </Flex>

        <Flex mt="32px" w="100%" maxW="640px" justify="flex-end">
          <Button
            bg="#00AAFF"
            color="#FFFFFF"
            borderRadius="4px"
            px="24px"
            h="40px"
            fontFamily="'Open Sans', sans-serif"
            fontSize="15px"
            fontWeight={600}
            _hover={{ bg: '#0099EE' }}
            onClick={openPasswordModal}
          >
            {t('backstage.statistics.save', '儲存')}
          </Button>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="540px">
          <ModalHeader p="24px 24px 16px 24px">
            <Text fontFamily="'Open Sans', sans-serif" fontWeight={600} fontSize="18px" color="text.main">
              請輸入密碼以儲存設定
            </Text>
          </ModalHeader>

          <ModalBody p="0 24px 16px 24px">
            <FormControl w="80%" isInvalid={password.isError}>
              <InputGroup>
                <InputLeftElement color="text.main">
                  <Text fontFamily="'Open Sans', sans-serif" fontWeight={600} fontSize="14px">
                    密碼
                  </Text>
                </InputLeftElement>
                <Input
                  type={password.visible ? 'text' : 'password'}
                  placeholder="請輸入密碼"
                  value={password.text}
                  onChange={(e) => setPassword((prev) => ({ ...prev, text: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && password.text) handleConfirm();
                  }}
                />
                <InputRightElement
                  cursor="pointer"
                  onClick={() => setPassword((prev) => ({ ...prev, visible: !prev.visible }))}
                >
                  {password.visible ? <ViewOffIcon color="primary.main" /> : <ViewIcon color="primary.main" />}
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{password.errorMsg}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter p="0 24px 24px 24px" gap="8px">
            <Button variant="secondary" onClick={onClose}>
              返回
            </Button>
            <Button
              bg="#00AAFF"
              color="#FFFFFF"
              borderRadius="4px"
              px="16px"
              _hover={{ bg: '#0099EE' }}
              isLoading={saving}
              loadingText={t('backstage.statistics.saving', '儲存中')}
              disabled={!password.text}
              onClick={handleConfirm}
            >
              確認儲存
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default StatisticsManagerPage;
